import React from 'react';
import classNames from 'classnames';
import { Text } from '@/components/atoms/AppText';
import { APP_BG, APP_TEXT } from '@/constant/theme/colors.constant';

export interface TabNavOption {
  /** Unique value used for active state comparison */
  value: string;
  /** Human-readable label */
  label: string;
  /** Optional icon rendered before the label */
  icon?: React.ReactNode;
}

export interface TabNavProps {
  /** Tab options to render */
  tabs: TabNavOption[];
  /** Currently active tab value */
  active: string;
  /** Called when a tab is clicked */
  onChange: (value: string) => void;
  /** Extra Tailwind classes applied to the outer container */
  className?: string;
}

/**
 * TabNav — horizontal pill-style tab navigation bar.
 *
 * Fully generic: pass any `tabs` array and it renders them. The active tab
 * is highlighted with the app primary colour. Works in any page or panel.
 *
 * Usage:
 * ```tsx
 * const TABS: TabNavOption[] = [
 *   { value: "overview", label: "Overview", icon: <LuLayoutGrid className="w-3.5 h-3.5" /> },
 *   { value: "logs",     label: "Logs",     icon: <LuScrollText className="w-3.5 h-3.5" /> },
 * ];
 *
 * <TabNav tabs={TABS} active={activeTab} onChange={setActiveTab} />
 * ```
 */
export const TabNav = ({ tabs, active, onChange, className }: TabNavProps) => {
  return (
    <div
      className={classNames(
        'flex items-center gap-1 overflow-x-auto scrollbar-none px-3 py-2',
        APP_BG.elevated,
        className,
      )}
    >
      {tabs.map((tab) => {
        const isActive = tab.value === active;
        return (
          <button
            key={tab.value}
            type="button"
            onClick={() => onChange(tab.value)}
            className={classNames(
              'flex shrink-0 items-center gap-1.5 rounded-lg px-2.5 py-1.5 sm:px-3',
              'text-[12px] font-semibold transition-all duration-150',
              isActive
                ? 'bg-primary text-white shadow-sm'
                : `${APP_TEXT.soft} hover:text-primary`,
            )}
          >
            {tab.icon && tab.icon}
            <span className="hidden sm:inline">{tab.label}</span>
          </button>
        );
      })}
    </div>
  );
};
